import type { ExperienceEventFragment } from '@/features/events/fragments/ExperienceEventFragment.generated';
import type { EventMetadataInput } from '@/types/gql.generated';

export type EventMetadata = ExperienceEventFragment['metadata'];

/**
 * Maps an event's decorated metadata onto the input every metadata-writing
 * mutation takes — `createEvents`, `updateEvent` and `updateEventMetadata` all
 * embed these same seven fields.
 *
 * Shared because the null handling is not uniform and each rule has a reason a
 * caller re-deriving it by hand would have to rediscover:
 *
 * - `categoryId` coalesces to null, which clears the category. There is no
 *   inherit tier for it, so absent genuinely means none.
 * - `kind` and `emoji` stay bare. They are *this experience's override* of the
 *   event's own value, where null means "clear the override" and undefined means
 *   "leave it alone" — and an event decorated from a metadata-less shape has no
 *   key at all. Coercing those to null would clear a real override on any
 *   unrelated metadata edit.
 * - `notes` stays bare for the same reason, though not because of an inherit
 *   tier: it has none. It is bare because omitting it has to mean "leave the
 *   note alone" for the surfaces that patch one other field, which the modal
 *   relies on when it saves shading or a category on a fed-in event.
 */
export const toEventMetadataInput = (
  metadata: EventMetadata
): EventMetadataInput => ({
  categoryId: metadata.category?.id ?? null,
  hidden: metadata.hidden,
  kind: metadata.kind,
  emoji: metadata.emoji,
  notes: metadata.notes,
  labelIds: metadata.labels.map((label) => label.id),
  whoLabelIds: metadata.whoLabels.map((label) => label.id),
});
